import { usePermissions } from 'hooks/usePermissions';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { SpotlightPermission } from 'types/enums';
import { getAnalytics } from '../api';
import EmptyState from '../components/empty';
import DeliveryStatusChart from '../views/Analytics/DeliveryStatus';
import AnalyticsFilters from '../views/Analytics/Filters';
import AnalyticsHeader from '../views/Analytics/Header';
import ProductSupplyChart from '../views/Analytics/ProductSupply';
import { useAnalyticsStyles } from '../views/Analytics/Styles';
import AnalyticsSummary from '../views/Analytics/Summary';
import AnalyticsTable from '../views/Analytics/Table';

const Analytics: React.FC = () => {
  const classes = useAnalyticsStyles();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [retry, setRetry] = useState(true);
  const [shopID, setShopID] = useState<number | undefined>(undefined);
  const { isPermitted } = usePermissions();
  const canViewAllShops = isPermitted(
    [SpotlightPermission.ALL_SHOPS, SpotlightPermission.VIEW_ANALYTICS, SpotlightPermission.SEE_ALL],
    'all'
  );

  useEffect(() => {
    if (!shopID && canViewAllShops) {
      setShopID(1); // set a default store id for super agents
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAllShops]);

  const {
    isLoading,
    data,
    error = {},
  } = useQuery<any, any>(
    ['getAnalytics', startDate, endDate, shopID],
    () => getAnalytics({ startDate, endDate, shopID }),
    {
      refetchInterval: retry ? 300000 : false,
      refetchIntervalInBackground: true,
      onError: () => setRetry(false),
    }
  );

  if (isLoading)
    return (
      <div className={`analytics ${classes.root}`}>
        <div className="header-wrapper">
          <AnalyticsHeader
            endDate={endDate}
            startDate={startDate}
            setEndDate={setEndDate}
            setstartDate={setStartDate}
            setSelectedIndex={setSelectedIndex}
            selectedIndex={selectedIndex}
            data={undefined}
            shopID={shopID || 0}
            setShopID={setShopID}
          />
        </div>
        <EmptyState loading title="Loading Analytics..." />
      </div>
    );

  if (!data)
    return (
      <div className={`analytics ${classes.root}`}>
        <div className="header-wrapper">
          <AnalyticsHeader
            endDate={endDate}
            startDate={startDate}
            setEndDate={setEndDate}
            setstartDate={setStartDate}
            setSelectedIndex={setSelectedIndex}
            selectedIndex={selectedIndex}
            shopID={shopID || 0}
            setShopID={setShopID}
          />
        </div>
        <EmptyState title={`${error?.response?.data?.message ?? 'No data found!'}`} />
      </div>
    );

  return (
    <div className="analytics">
      <AnalyticsHeader
        endDate={endDate}
        startDate={startDate}
        setEndDate={setEndDate}
        setstartDate={setStartDate}
        setSelectedIndex={setSelectedIndex}
        selectedIndex={selectedIndex}
        data={data.orderSummary}
        shopID={shopID || 0}
        setShopID={setShopID}
      />
      <div className={`${classes.filterWrapper} ${classes.sectionMobile}`}>
        <AnalyticsFilters
          endDate={endDate}
          startDate={startDate}
          setEndDate={setEndDate}
          setstartDate={setStartDate}
          setSelectedIndex={setSelectedIndex}
          selectedIndex={selectedIndex}
          data={data?.orderSummary}
          shopID={shopID || 0}
          setShopID={setShopID}
        />
      </div>
      <div className={classes.summarySection}>
        <AnalyticsSummary caption="Not Started" value={`${data?.orderSummary?.notStarted ?? 0}`} />
        <AnalyticsSummary caption="In-Progress (Shopping)" value={`${data?.orderSummary?.inProgress ?? 0}`} />
        <AnalyticsSummary caption="In-Progress (Awaiting Driver)" value={`${data?.orderSummary?.pendingDriver ?? 0}`} />
        <AnalyticsSummary caption="In-Progress (Delivery)" value={`${data?.orderSummary?.onTheWay ?? 0}`} />
        <AnalyticsSummary caption="Completed" value={`${data?.orderSummary?.completed ?? 0}`} />
        <AnalyticsSummary caption="Completed Early" value={`${data?.orderSummary?.completedEarly ?? 0}`} />
        <AnalyticsSummary caption="Completed On-Time" value={`${data?.orderSummary?.completedOnTime ?? 0}`} />
        <AnalyticsSummary caption="Completed Late" value={`${data?.orderSummary?.completedLate ?? 0}`} />
      </div>
      <div className={classes.summarySection}>
        <AnalyticsSummary caption="Total Items Ordered" value={`${data?.deliverySummary?.itemsOrdered ?? 0}`} />
        <AnalyticsSummary caption="Total Items Supplied" value={`${data?.deliverySummary?.itemsSupplied ?? 0}`} />
        <AnalyticsSummary caption="Fulfilment Rate (%)" value={`${data?.deliverySummary?.fulfillmentRate ?? 0}`} />
        <AnalyticsSummary
          caption="Perfectly Supplied (Within SLA)"
          value={`${data?.deliverySummary?.perfectlySupplied ?? 0}`}
        />
        <AnalyticsSummary
          caption="Avg. Delivery Time per km (minutes)"
          value={`${data?.deliverySummary?.avgDeliveryTime ?? 0}`}
        />
        <AnalyticsSummary caption="ABS (Supplied)" value={`${data?.deliverySummary?.avgBasketSize ?? 0}`} />
        <AnalyticsSummary
          caption="Queueing & Payment Time (minutes)"
          value={`${data?.deliverySummary?.avgTillTime ?? 0}`}
        />
        <div className={`placeholder ${classes.summaryCard}`} />
      </div>
      <div className={classes.tableSection}>
        <AnalyticsTable title="Orders Per Timeslot (Complete vs InComplete)" data={data?.ordersPerTimeslot} />
      </div>
      <div className={classes.tableSection}>
        <DeliveryStatusChart data={data?.orderSummary} />
        <ProductSupplyChart data={data?.deliverySummary} />
      </div>
    </div>
  );
};

export default Analytics;
